import type { BuildType } from 'src/enums/build/build-type';

import { getTawkUrl } from './config-helper';

type TawkMethod =
  | 'maximize'
  | 'minimize'
  | 'showWidget'
  | 'hideWidget'
  | 'getStatus'
  | 'onStatusChange'
  | 'onLoad';

type TawkHandler = (...args: unknown[]) => void;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Tawk_API: Partial<Record<TawkMethod, (...args: unknown[]) => unknown>>;
    Tawk_LoadStart?: Date;
  }
}

let queue: { key: TawkMethod; args?: unknown[] }[] = [];
let startedInit = false;
let finishedInit = false;
const handlers: Partial<Record<TawkMethod, TawkHandler[]>> = {
  onStatusChange: [],
};

function init(buildType: BuildType, siteId: string) {
  if (startedInit || typeof window === `undefined`) {
    return;
  }

  startedInit = true;

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();

  const s1 = document.createElement('script');
  const s0 = document.getElementsByTagName('script')[0];
  s1.async = true;
  s1.src = getTawkUrl(buildType, siteId);
  s1.charset = 'UTF-8';
  s1.setAttribute('crossorigin', '*');
  s0?.parentNode?.insertBefore(s1, s0);

  (Object.keys(handlers) as (keyof Partial<Record<TawkMethod, TawkHandler[]>>)[]).forEach(
    (event) => {
      window.Tawk_API[event] = (...args) => {
        if (handlers[event]) {
          handlers[event].forEach((handler) => {
            if (handler) {
              handler(...args);
            }
          });
        }
      };
    }
  );

  window.Tawk_API.onLoad = () => {
    finishedInit = true;
    queue.forEach(runCall);
    queue = [];
  };
}

function runCall({ key, args }: { key: TawkMethod; args?: unknown[] }) {
  try {
    window.Tawk_API[key]?.(...(args ?? []));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to run tawk.${key}(...${JSON.stringify(args)}) on tawk ${Object.keys(window.Tawk_API).join(', ')}`,
      e
    );
  }
}

const conditionalMethodForward =
  (key: TawkMethod) =>
  (...args: unknown[]) => {
    const call = { key, args };
    if (finishedInit) {
      runCall(call);
    } else {
      queue.push(call);
    }
  };

const failableMethodForward =
  (key: TawkMethod) =>
  (...args: unknown[]): unknown => {
    if (typeof window === `undefined`) {
      return;
    }
    const method = window.Tawk_API?.[key];
    return method?.(...args);
  };

const addHandler = (event: TawkMethod) => (handler: TawkHandler) => {
  handlers[event]?.push(handler);

  // return index of handler
  return handlers[event] && handlers[event]?.length - 1;
};

const removeHandler = (event: TawkMethod) => (handlerId: number | undefined) => {
  if (handlers[event] && handlerId !== undefined) {
    // eslint-disable-next-line @typescript-eslint/no-array-delete
    delete handlers[event][handlerId]; // Intentionally leave a sparse hole; maintain the indices of remaining handlers
  }
};

export default {
  init,
  maximize: conditionalMethodForward('maximize'),
  minimize: conditionalMethodForward('minimize'),
  showWidget: conditionalMethodForward('showWidget'),
  hideWidget: conditionalMethodForward('hideWidget'),
  getStatus: failableMethodForward('getStatus'),
  addStatusChangeHandler: addHandler('onStatusChange'),
  removeStatusChangeHandler: removeHandler('onStatusChange'),
};
